import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FolderKanban, Building2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface Client {
  id: string;
  name: string;
  primaryColor: string;
}

export function NewProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedOrgId = searchParams.get('organizationId');

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedClient, setSelectedClient] = useState(preselectedOrgId || '');

  useEffect(() => {
    async function fetchClients() {
      try {
        setIsLoadingClients(true);
        const response = await api.get<Client[]>('/organizations');
        setClients(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load clients:', err);
        toast.error('Failed to load clients');
      } finally {
        setIsLoadingClients(false);
      }
    }
    fetchClients();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/projects', {
        organizationId: selectedClient,
        name,
        description: description || undefined,
      });
      toast.success('Project created successfully!');
      navigate('/projects');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-3xl font-bold">New Project</h1>
        <p className="text-text-secondary mt-1">
          Create a new project for a client
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Selection */}
          <Card hover={false}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent" />
                Select Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-accent" />
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <p>No clients found. Create a client first.</p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-4"
                    onClick={() => navigate('/clients/new')}
                  >
                    Create Client
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {clients.map((client) => (
                    <motion.button
                      key={client.id}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedClient(client.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedClient === client.id
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: client.primaryColor }}
                        >
                          {client.name.charAt(0)}
                        </div>
                        <span className="font-medium truncate">{client.name}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Details */}
          <Card hover={false}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-accent" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Project Name"
                placeholder="Website Redesign"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text-secondary">
                  Description
                </label>
                <textarea
                  placeholder="Brief description of the project..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl bg-bg-secondary border border-border
                             text-text placeholder:text-text-muted resize-none
                             focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20
                             transition-all"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!selectedClient || !name}
            >
              Create Project
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
